import { Button, Heading } from "@ariakit/react";
import { CopyToClipboardButton, MiddotSeparated } from "@indietabletop/appkit";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { Fragment } from "react/jsx-runtime";
import { useSearchParams } from "wouter";
import type { NameGenerator } from "../../engine/NameGenerator.ts";
import { Page } from "../Page/Page.tsx";
import { useLatestRuleset } from "../rulesets.tsx";
import { fromPattern } from "./fromPattern.ts";
import * as css from "./style.css.ts";

function omitFromParams(params: URLSearchParams, omit: string) {
  const returnParams = new URLSearchParams(params);
  returnParams.delete(omit);
  return returnParams;
}

function GeneratedName(props: {
  generator: NameGenerator;
  generated: Record<string, string>;
  generatePartial: (key: string) => void;
}) {
  const { generator, generated, generatePartial } = props;
  const words = fromPattern(generator.pattern);

  return (
    <>
      {words.map((word, wIndex) => {
        return (
          <Fragment key={word.join("")}>
            {wIndex !== 0 && " "}

            <span className={css.name.hyphenate}>
              {word.map((segment, sIndex) => {
                if (segment === "\u00AD") {
                  return segment;
                }

                return (
                  <Button
                    key={sIndex}
                    onClick={() => void generatePartial(segment)}
                    className={css.name.segment}
                    style={assignInlineVars({
                      [css.delayOffset]: `${wIndex + sIndex}`,
                    })}
                  >
                    {generated[segment]}
                  </Button>
                );
              })}
            </span>
          </Fragment>
        );
      })}
    </>
  );
}

export function NameGeneratorPage() {
  const { orcNameGenerator, nameGenerators } = useLatestRuleset();
  const [params, setParams] = useSearchParams();

  const generator =
    nameGenerators.get(params.get("generator") ?? "git").valueOrNull() ??
    orcNameGenerator;

  const generated = generator.fromSearchParams(params);

  const generatePartial = (key: string) => {
    const newParams = generator.generateSearchParams(
      omitFromParams(params, key)
    );

    setParams(newParams, { replace: true });
  };

  return (
    <Page backHref="~/" title="Names" centerChildren>
      <MiddotSeparated className={css.options}>
        {nameGenerators.toArray().map((g) => {
          return (
            <Button
              key={g.id}
              onClick={() => void setParams(g.generateSearchParams())}
              className={css.option({ selected: generator.id === g.id })}
            >
              {g.name}
            </Button>
          );
        })}
      </MiddotSeparated>

      <Heading className={css.heading}>
        {"Your "}
        {generator.name}&apos;s
        {" name is"}
      </Heading>

      <div className={css.name.container}>
        {generated ? (
          <GeneratedName
            generator={generator}
            generated={generated}
            generatePartial={generatePartial}
          />
        ) : (
          "???"
        )}
      </div>

      <div className={css.actions.container}>
        <div className={css.actions.hint}>
          (Pssst! Tap name parts to change &apos;em!)
        </div>

        <Button
          className={css.actions.generate}
          onClick={() => void setParams(generator.generateSearchParams())}
        >
          {generated ? "Go again!" : "Generate"}
        </Button>

        {generated && (
          <CopyToClipboardButton
            className={css.actions.copy}
            value={generator.join(
              generated.start,
              generated.middle,
              generated.end
            )}
            revertAfterMs={1500}
          >
            Copy
          </CopyToClipboardButton>
        )}
      </div>
    </Page>
  );
}
